import { zodResolver } from "@hookform/resolvers/zod";
import type { Operatory, Provider, ReasonForVisit } from "@prisma/client";
import { PatientStatus } from "@prisma/client";
import "@smastrom/react-rating/style.css";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import dayjs from "@calcom/dayjs";
import {
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsBackgroundTransparent,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import CustomBranding from "@calcom/lib/CustomBranding";
import classNames from "@calcom/lib/classNames";
import useTheme from "@calcom/lib/hooks/useTheme";
import { detectBrowserTimeFormat, setIs24hClockInLocalStorage, TimeFormat } from "@calcom/lib/timeFormat";
import { Button, Form, TextField } from "@calcom/ui";

import { timeZone as localStorageTimeZone } from "@lib/clock";
import { SpecialityOptions } from "@lib/constant";
import useRouterQuery from "@lib/hooks/useRouterQuery";
import { searchPatient } from "@lib/practice";
import { formatBirthDate, formatPhoneNumber } from "@lib/practice/helper";

import type { AvailabilityPageProps } from "../../../pages/[user]/[type]";
import UserAvatar from "../../../public/user.png";

type PatientFormValues = {
  firstName: string;
  lastName: string;
  birthdate: string;
};

const Toaster = dynamic(() => import("react-hot-toast").then((mod) => mod.Toaster), { ssr: false });
const SlotPicker = dynamic(() => import("../SlotPicker").then((mod) => mod.SlotPicker), {
  ssr: false,
  loading: () => <div className="mt-8 px-4 pb-4 sm:mt-0 sm:p-4 md:min-w-[300px] md:px-5 lg:min-w-[455px]" />,
});

type AppointmentType = ReasonForVisit & { operatories: Operatory[]; providers: Provider[] };

const AvailabilityPage = ({ profile, eventType, appointmentTypes, ...restProps }: AvailabilityPageProps) => {
  const router = useRouter();
  const isEmbed = useIsEmbed(restProps.isEmbed);
  useTheme(profile.theme);
  const availabilityDatePickerEmbedStyles = useEmbedStyles("availabilityDatePicker");
  //TODO: Plan to remove shouldAlignCentrallyInEmbed config
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const isBackgroundTransparent = useIsBackgroundTransparent();

  const { time } = useRouterQuery("time");
  const [page, setPage] = useState(0);
  const [timeZone, setTimeZone] = useState<string>();
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(detectBrowserTimeFormat);
  const [newPatient, setPatientStatus] = useState<boolean>(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [filteredTypes, setFilteredTypes] = useState<Array<AppointmentType>>([]);
  const [currentType, setCurrentType] = useState<AppointmentType | null>(null);
  const [providers, setProviders] = useState<Array<Provider>>([]);
  const [operatories, setOperatories] = useState<Array<number>>([]);
  const [minLeadTime, setMinLeadTime] = useState<number>(0);
  const [maxLeadTime, setMaxLeadTime] = useState<number>(87600);
  const [weekendOperatories, setWeekendOperatories] = useState<Array<number>>([]);
  const [appointmentTypeId, setAppointmentTypeId] = useState<number | null>(null);
  const [patientSearch, setPatientSearch] = useState(false);

  const onTimeFormatChange = (is24Hours: boolean) => {
    setTimeFormat(is24Hours ? TimeFormat.TWENTY_FOUR_HOUR : TimeFormat.TWELVE_HOUR);
    setIs24hClockInLocalStorage(is24Hours);
  };

  useEffect(() => {
    setTimeZone(localStorageTimeZone() || dayjs.tz.guess());
  }, []);

  const isFitnessType = eventType.isFitnessType;

  const patientForm = useForm<PatientFormValues>({
    defaultValues: {
      birthdate: isFitnessType ? "01/01/1990" : "",
    },
    resolver: zodResolver(
      z.object({
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        birthdate: z
          .string()
          .refine((value) => dayjs(value, "MM/DD/YYYY", true).isValid(), "Birthday is not valid"),
      })
    ),
  });

  const { register } = patientForm;

  const shouldHideProviderSelection = (apptType: AppointmentType | null) =>
    Boolean(profile.hideProviderPage || apptType?.hideProviderSelection);

  const getPatientInfo = async (values: PatientFormValues) => {
    const params = {
      ...values,
      birthday: dayjs(values.birthdate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      userName: profile?.username,
    };

    let isNewPatient = true;

    if (isFitnessType) {
      setPatientStatus(true);
    } else {
      setPatientSearch(true);
      const patientList = await searchPatient(params);
      setPatientStatus(patientList.length < 1);
      setPatientSearch(false);
      isNewPatient = patientList.length < 1;

      if (patientList.length > 0) {
        setPhone(
          patientList[0].phone ||
            patientList[0].cellPhone ||
            patientList[0].homePhone ||
            patientList[0].workPhone
        );
        setEmail(patientList[0].email);
      } else {
        setPhone("");
        setEmail("");
      }
    }

    setFirstName(values.firstName);
    setLastName(values.lastName);
    setBirthday(values.birthdate);

    const patientStatus = isNewPatient ? PatientStatus.NEW : PatientStatus.RETURNING;
    const types = appointmentTypes.filter((apptType) => {
      const age = dayjs().diff(dayjs(values.birthdate), "year");
      const appropriateAge =
        !apptType.ageLimited || (apptType.upperAgeLimit >= age && apptType.lowerAgeLimit <= age);
      return apptType.patientStatus == patientStatus && appropriateAge;
    });
    setFilteredTypes(types);

    if (!eventType.hideAppointmentType || (eventType.hideAppointmentType && types.length == 0)) {
      setPage(1);
    } else {
      if (types.length > 0) {
        const currentType = types[0];
        setAppointmentType(currentType);
        setOperatories(currentType.operatories.map((op) => op.opNum));
        setWeekendOperatories(currentType.weekendOps);
        setAppointmentTypeId(currentType.apptTypeId);
        setMinLeadTime(currentType.minLeadTime);
        setMaxLeadTime(currentType.maxLeadTime);

        if (currentType?.redirectLink) {
          router.push({
            pathname: `/${currentType.redirectLink}`,
          });
          setCurrentType(null);
        } else {
          if (shouldHideProviderSelection(currentType)) {
            setProviders(currentType?.providers || []);
            setPage(3);
            return;
          }
          setPage(2);
        }
      }
    }
  };

  const setAppointmentType = (apptType: AppointmentType) => {
    setCurrentType(apptType);
    setOperatories(apptType.operatories.map((op) => op.opNum));
    setWeekendOperatories(apptType.weekendOps);
    setAppointmentTypeId(apptType.apptTypeId);
    setMinLeadTime(apptType.minLeadTime);
    setMaxLeadTime(apptType.maxLeadTime);
  };

  const handleAppointmentType = () => {
    if (currentType?.redirectLink) {
      router.push({
        pathname: `/${currentType.redirectLink}`,
      });
      setCurrentType(null);
    } else {
      if (shouldHideProviderSelection(currentType)) {
        setProviders(currentType?.providers || []);
        setPage(3);
        return;
      }
      setPage(2);
    }
  };

  const navigateBookingPage = () => {
    const providerId = providers.find((p) => p.providerId == Number(router.query.prov))?.id;
    console.log("Navigating...", providerId);

    router.push({
      pathname: router.pathname.endsWith("/embed") ? "../book" : "book",
      query: {
        ...router.query,
        firstName,
        lastName,
        birthday,
        email,
        phone,
        reason: currentType?.id,
        provider: providerId,
        status: newPatient ? PatientStatus.NEW : PatientStatus.RETURNING,
        type: eventType.id,
        slug: eventType.slug,
      },
    });
  };

  return (
    <>
      <Head>
        <title>
          {eventType.title} | {profile.name}
        </title>
      </Head>
      <CustomBranding
        lightVal={profile.brandColor}
        darkVal={profile.darkBrandColor}
        titleVal={profile.titleColor}
      />
      <main
        className={classNames(
          "flex flex-col md:mx-4",
          shouldAlignCentrally ? "items-center" : "items-start",
          !isEmbed && classNames("mx-auto my-0 ease-in-out md:my-24")
        )}>
        <div className="border-gray-200">
          <div className="bg-title flex flex-row items-center justify-between rounded-tl-md rounded-tr-md border border-b-0 border-gray-200 px-8 py-4">
            <div className="flex items-center">
              <div
                className={classNames(
                  "mx-4 rounded",
                  eventType.hideLogoFrame ? "w-56 py-1" : "w-48 bg-white py-1.5"
                )}>
                <img
                  src={eventType.logo || ""}
                  alt=""
                  className={classNames("mx-auto w-auto", eventType.hideLogoFrame ? "h-14" : "h-12")}
                />
              </div>
              {!eventType.showGoogleReview && !eventType.showAddress && (
                <p className="font-bold text-white">{eventType.hideLogoFrame ? "" : profile.name}</p>
              )}
            </div>
            {eventType.showGoogleReview && (
              // <div className="flex items-center">
              //   <div className="flex flex-col items-end">
              //     <Rating style={{ maxWidth: 100 }} value={4.9} readOnly={true} itemStyles={ratingStyles} />
              //     <p className="mt-1 text-right text-sm font-medium text-gray-700">
              //       4.9 / 5 Stars - {eventType.slug == "fenton" ? 321 : 509} Google Reviews
              //     </p>
              //   </div>
              //   <a target="_blank" href={eventType.reviewLink}>
              //     <Image src={Review} alt="" className="ml-4 h-10 w-auto" />
              //   </a>
              // </div>
              <p className="text-sm font-medium text-white">Address : {eventType.address}</p>
            )}
            {eventType.showAddress && (
              <div>
                <p className="mb-2 text-right font-bold uppercase text-white">{profile.name}</p>
                <p className="text-sm font-medium text-white">{eventType.address}</p>
              </div>
            )}
            {eventType.showPhoneNumber && (
              <p className="font-bold uppercase text-white">{formatPhoneNumber(eventType.contact || "")}</p>
            )}
          </div>
          <div
            style={availabilityDatePickerEmbedStyles}
            className={classNames(
              isBackgroundTransparent ? "" : "flex flex-col bg-white px-8 py-4 md:px-16 md:py-8",
              "border-bookinglightest md:rounded-bl-md md:rounded-br-md md:border",
              isEmbed && "mx-auto"
            )}>
            <div
              className={classNames(
                "flex flex-wrap items-center justify-between gap-4 md:mb-8",
                profile.showAvailabilityForm && page == 3 ? "md:mb-4" : "",
                isFitnessType ? "md:mb-0" : ""
              )}>
              {!isFitnessType && (
                <h2 className="mb-2 text-lg font-bold text-gray-800 md:text-2xl">
                  {page == 0 ? "Who would you like to schedule an appointment for?" : "Schedule Appointment"}
                </h2>
              )}

              {profile.showAvailabilityForm && page == 3 && (
                <div className="border-bookinglightest flex flex-wrap items-center rounded-md border py-2 px-4">
                  <div className="border-bookinglightest border-r pr-6 md:flex-col">
                    <p className="text-center text-xs lg:text-sm">For more available times</p>
                    <p className="text-center text-xs lg:text-sm">email or call our office</p>
                  </div>
                  <div className="pl-6">
                    <div className="flex gap-4">
                      <img src="/phone-call.svg" alt="email" />
                      <p className="text-brand cursor-pointer text-xs lg:text-sm">
                        {formatPhoneNumber(profile.phone || "")}
                      </p>
                    </div>
                    <div className="flex gap-4">
                      <img src="/email.svg" alt="email" />
                      <p className="text-brand cursor-pointer text-xs lg:text-sm">{profile.email}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-2 mb-2 md:mb-6 md:min-w-[39rem]">
              {page == 0 && (
                <Form form={patientForm} handleSubmit={getPatientInfo}>
                  <div className="mb-4">
                    <p className="mb-2 text-sm font-medium text-gray-600">
                      {isFitnessType ? "Client Name" : "Patient Name"}
                    </p>
                    <div className="flex flex-row gap-4">
                      <TextField
                        containerClassName="flex-1"
                        className="w-full"
                        label=""
                        placeholder="First Name"
                        {...register("firstName")}
                        onChange={(event) => {
                          patientForm.setValue("firstName", event.target.value.trim());
                        }}
                      />
                      <TextField
                        containerClassName="flex-1"
                        className="w-full"
                        label=""
                        placeholder="Last Name"
                        {...register("lastName")}
                        onChange={(event) => {
                          patientForm.setValue("lastName", event.target.value.trim());
                        }}
                      />
                    </div>

                    {!isFitnessType && (
                      <>
                        <p className="mt-4 text-sm font-medium text-gray-600">Date of Birth</p>
                        <div className="flex">
                          <TextField
                            containerClassName="flex-1"
                            className="w-full"
                            label=""
                            placeholder="MM/DD/YYYY"
                            {...register("birthdate")}
                            onChange={(event) => {
                              patientForm.setValue("birthdate", formatBirthDate(event.target.value));
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="border-bookinglightest border-b pb-4" />
                  <div className="flex flex-row justify-end">
                    <Button
                      className={classNames(
                        "bg-darkmodebrand mt-4 h-10 w-28 justify-center text-xs font-bold uppercase ",
                        "hover:bg-darkmodebrand hover:opacity-90 disabled:bg-gray-400 md:mt-6 md:w-56 md:text-sm"
                      )}
                      loading={patientSearch}
                      type="submit">
                      <p>Next</p>
                    </Button>
                  </div>
                </Form>
              )}

              {page == 1 && (
                <div>
                  {filteredTypes.length > 0 && (
                    <p className="mb-4 max-w-[39rem] text-sm font-medium text-gray-600">
                      {eventType.instruction ? (
                        <span dangerouslySetInnerHTML={{ __html: eventType.instruction }} />
                      ) : (
                        <span>{isFitnessType ? "Select a class" : "What can we help you with?"}</span>
                      )}
                    </p>
                  )}
                  <div
                    className={classNames(
                      "border-bookinglightest relative grid grid-cols-2 gap-4 pb-6",
                      filteredTypes.length ? "" : "min-h-40"
                    )}>
                    {!filteredTypes.length && eventType.ageLimitMessage && (
                      <div className="absolute top-1/2 left-1/2 w-[80%] -translate-x-1/2 -translate-y-1/2">
                        <p className="text-center font-medium">{eventType.ageLimitMessage}</p>
                      </div>
                    )}
                    {filteredTypes.map((type, index) => {
                      const selected = type.id === currentType?.id;
                      return (
                        <div
                          key={index}
                          onClick={() => setAppointmentType(type)}
                          className={classNames(
                            "border-bookinglightest flex max-w-[20rem] cursor-pointer items-center justify-center rounded-lg border p-4",
                            selected ? "bg-brand text-white" : ""
                          )}>
                          <p className="text-center">{type.name}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-bookinglightest border-b pb-4">
                    <p className="max-w-[625px] text-sm text-gray-700">
                      {currentType?.availableOffice &&
                        `This appointment is not available at this location, however it's available on ${currentType?.availableOffice}. Click the button below "Redirect" to book at that location`}
                      {currentType?.description}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-row justify-between">
                    <Button
                      className={classNames(
                        "h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800",
                        "hover:bg-darkmodebrand  hover:border-0 hover:text-white hover:opacity-90 md:w-56 md:text-sm"
                      )}
                      onClick={() => setPage(0)}>
                      <p>Back</p>
                    </Button>
                    <Button
                      className={classNames(
                        "bg-darkmodebrand h-10 w-28 justify-center text-xs font-bold uppercase",
                        "hover:bg-darkmodebrand hover:opacity-90 disabled:bg-gray-400 md:w-56 md:text-sm"
                      )}
                      disabled={!currentType?.id}
                      onClick={handleAppointmentType}>
                      <p>{currentType?.availableOffice ? "Redirect" : "Next"}</p>
                    </Button>
                  </div>
                </div>
              )}

              {page == 2 && (
                <div>
                  <h3 className="mb-4 text-lg font-bold text-gray-700">Which provider would you like see?</h3>
                  <div className="border-bookinglightest grid grid-cols-2 gap-4 border-b pb-6">
                    {currentType?.providers.map((provider, index) => {
                      const selected = providers.some((item) => provider.id == item.id);
                      const speciality = SpecialityOptions.find(
                        (option) => option.value == provider.speciality
                      );

                      return (
                        <div
                          key={index}
                          onClick={() => setProviders([provider])}
                          className={classNames(
                            "border-bookinglightest flex cursor-pointer flex-row items-center gap-2 rounded-lg border p-2",
                            selected ? "bg-brand text-white" : ""
                          )}>
                          <Image
                            src={provider.avatar || UserAvatar}
                            alt=""
                            width={100}
                            height={100}
                            className="mr-4 h-12 w-12 rounded-full"
                          />
                          <div>
                            <p className="font-bold">{provider.name}</p>
                            <p className="text-sm">{speciality?.label}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 flex flex-row justify-between">
                    <Button
                      className={classNames(
                        "h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800",
                        "hover:bg-darkmodebrand hover:border-0 hover:text-white hover:opacity-90 md:w-56 md:text-sm"
                      )}
                      onClick={() => setPage(1)}>
                      <p>Back</p>
                    </Button>
                    <Button
                      className={classNames(
                        "bg-darkmodebrand h-10 w-28 justify-center text-xs font-bold uppercase",
                        "hover:bg-darkmodebrand hover:opacity-90 disabled:bg-gray-400 md:w-56 md:text-sm"
                      )}
                      disabled={!providers.length}
                      onClick={() => setPage(3)}>
                      <p>Next</p>
                    </Button>
                  </div>
                </div>
              )}

              {page == 3 && (
                <div>
                  <div className="md:flex">
                    <SlotPicker
                      weekStart={
                        typeof profile.weekStart === "string"
                          ? ([
                              "Sunday",
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                              "Saturday",
                            ].indexOf(profile.weekStart) as 0 | 1 | 2 | 3 | 4 | 5 | 6)
                          : profile.weekStart /* Allows providing weekStart as number */
                      }
                      eventType={eventType}
                      timeFormat={timeFormat}
                      onTimeFormatChange={onTimeFormatChange}
                      timeZone={timeZone}
                      providers={providers}
                      operatories={operatories}
                      weekendOps={weekendOperatories}
                      duration={currentType?.duration || 60}
                      slotInterval={currentType?.slotInterval || currentType?.duration || 30}
                      apptTypeId={appointmentTypeId}
                      reasonId={currentType?.id}
                      practice={profile.practice}
                      minLeadTime={minLeadTime}
                      maxLeadTime={maxLeadTime}
                    />
                  </div>

                  <div className="mt-4 flex flex-row justify-between md:mt-0">
                    <Button
                      className={classNames(
                        "h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800",
                        "hover:bg-darkmodebrand hover:border-0 hover:text-white hover:opacity-90 md:w-56 md:text-sm"
                      )}
                      onClick={() =>
                        setPage(
                          shouldHideProviderSelection(currentType)
                            ? eventType.hideAppointmentType
                              ? 0
                              : 1
                            : 2
                        )
                      }>
                      <p>Back</p>
                    </Button>
                    <Button
                      className={classNames(
                        "h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 ",
                        "hover:bg-darkmodebrand hover:border-0 hover:text-white hover:opacity-90 md:w-56 md:text-sm"
                      )}
                      disabled={!time}
                      onClick={navigateBookingPage}>
                      <p>Next</p>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <Link
          href={eventType.groupHomeURL || "https://www.getcaresuite.com"}
          target="_blank"
          className="mt-8 text-center text-sm text-gray-600 hover:text-gray-900 md:text-base">
          Powered by {eventType.groupName}
        </Link>
      </main>
      <Toaster position="bottom-right" />
    </>
  );
};

export default AvailabilityPage;
